const { getOffsetTop } = window.BAO.Utils

class ProductFixedBar extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['action', 'cartIcon', 'notifyIcon', 'actionText']
  }

  static get requiredExternalElements () {
    return ['productInfo', 'addToCart']
  }

  connectedCallback () {
    super.connectedCallback()

    this.isTicking = false
    this.lastKnownY = window.scrollY

    this.requestTick = this.requestTick.bind(this)
    this.updateEl = this.updateEl.bind(this)
    this.updateFixedAction = this.updateFixedAction.bind(this)
    this.onFixedActionClick = this.onFixedActionClick.bind(this)

    this.setupFixedAction()
    this.updateEl()
  }

  disconnectedCallback () {
    super.disconnectedCallback()

    window.BAO.eventBus.removeEventListener(
      window.BAO.EVENTS.PRODUCT.OPTION_CHANGED,
      this.onVariantChange,
    )
  }

  setupListeners () {
    super.setupListeners()

    this.onScroll = this.onScroll.bind(this)
    this.onVariantChange = this.onVariantChange.bind(this)

    this.listeners.add(window, 'scroll', this.onScroll)

    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PRODUCT.OPTION_CHANGED,
      this.onVariantChange,
    )
  }

  /**
   * setupFixedAction
   * Sets up the fixed action and syncs it with the product form and BIS state.
   */
  setupFixedAction () {
    this.fixedAction = this.els.action.element

    if (!this.fixedAction) return

    this.canShowBackInStock = this.dataset.showBackInStock === 'true'

    this.productInfo = this.externalEls.productInfo?.element

    if (!this.productInfo) return

    this.fixedAction.addEventListener('click', this.onFixedActionClick)

    this.updateFixedAction()
  }

  get productAddToCart () {
    return this.externalEls.addToCart?.element || null
  }

  /**
   * onVariantChange
   * Updates the fixed action when the selected variant changes.
   */
  onVariantChange ({ detail }) {
    if (detail.sectionId !== this.productInfo.dataset.section) return

    this.updateFixedAction()
  }

  /**
   * onFixedActionClick
   * Handles Add to Bag and Notify Me clicks, including BIS field scrolling.
   */
  onFixedActionClick (event) {
    const productAddToCart = this.productAddToCart

    if (!productAddToCart) return

    if (!productAddToCart.disabled) {
      event.preventDefault()
      productAddToCart.click()
      return
    }

    if (!this.canShowBackInStock) {
      event.preventDefault()
      return
    }

    event.preventDefault()

    const emailInput = this.externalEls.bisEmail?.element

    if (!emailInput) return

    const emailRect = emailInput.getBoundingClientRect()

    // Keep the email field below the size selector/header area.
    const targetTop = window.innerHeight * 0.5

    window.scrollBy({
      top: emailRect.top - targetTop,
      behavior: 'smooth',
    })

    setTimeout(() => {
      emailInput.focus({
        preventScroll: true,
      })
    }, 500)
  }

  /**
   * updateFixedAction
   * - Updates the fixed button state, text and icons for availability and BIS.
   */
  updateFixedAction () {
    const productAddToCart = this.productAddToCart

    if (!productAddToCart || !this.fixedAction) return

    const text = this.els.actionText?.element
    const cartIcon = this.els.cartIcon?.element
    const notifyIcon = this.els.notifyIcon?.element

    const isAvailable = !productAddToCart.disabled

    if (isAvailable) {
      this.fixedAction.disabled = false
      this.fixedAction.type = 'submit'
      this.fixedAction.name = 'add'

      this.fixedAction.classList.add('btn-Button-addToBag')

      if (cartIcon) {
        cartIcon.hidden = false
      }

      if (notifyIcon) {
        notifyIcon.hidden = true
      }

      this.fixedAction.setAttribute(
        'aria-label',
        this.fixedAction.dataset.addToBagText,
      )

      if (text) {
        text.textContent = this.fixedAction.dataset.addToBagText
      }

      return
    }

    this.fixedAction.disabled = false
    this.fixedAction.type = 'button'
    this.fixedAction.removeAttribute('name')
    this.fixedAction.classList.remove('btn-Button-addToBag')

    // Show Notify Me when back-in-stock is enabled.
    if (this.canShowBackInStock) {
      if (cartIcon) {
        cartIcon.hidden = true
      }

      if (notifyIcon) {
        notifyIcon.hidden = false
      }

      this.fixedAction.setAttribute(
        'aria-label',
        this.fixedAction.dataset.notifyMeText,
      )

      if (text) {
        text.textContent = this.fixedAction.dataset.notifyMeText
      }

      return
    }

    if (cartIcon) {
      cartIcon.hidden = true
    }

    if (notifyIcon) {
      notifyIcon.hidden = true
    }

    this.fixedAction.setAttribute(
      'aria-label',
      this.fixedAction.dataset.soldOutText,
    )

    if (text) {
      text.textContent = this.fixedAction.dataset.soldOutText
    }
  }

  onScroll () {
    this.lastKnownY = window.scrollY
    this.requestTick()
  }

  requestTick () {
    if (!this.isTicking) requestAnimationFrame(this.updateEl)
    this.isTicking = true
  }

  updateEl () {
    this.isTicking = false

    this.setVisibility()
  }

  setVisibility () {
    this.setAttribute('aria-hidden', !this.shouldBeVisible)
  }

  get shouldBeVisible () {
    if (!this.externalEls?.top?.exists) return

    const topStart = this.externalEls.top.element
    const bottomEnd = this.externalEls.bottom.element

    if (
      this.lastKnownY > getOffsetTop(topStart) + topStart.offsetHeight &&
      this.lastKnownY < getOffsetTop(bottomEnd) - window.innerHeight
    ) {
      return true
    } else {
      return false
    }
  }
}

if (!customElements.get('product-fixed-bar')) {
  customElements.define('product-fixed-bar', ProductFixedBar)
}
